import { ChangeEvent, memo, useCallback, useMemo, useState } from 'react';
import { Box, Fallback, HStack, VStack } from '@cbhq/cds-web/layout';
import { TextLabel1, TextLabel2 } from '@cbhq/cds-web/typography';
import { css, cx } from '@emotion/css';
import { Banner } from '@cbhq/cds-web/banner/Banner';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { useAddLiquidityState } from '@/components/AddLiquidity/AddLiquidityProvider';
import { parseNumericInputValue } from '@/components/AddLiquidity/utils';
import { isValidNumericInputValue } from '@/components/AddLiquidity/utils';
import { format, DisplayType } from '@/utils/numberFormatter/formatNumbers';
import { NESTED_CARD_BORDER_RADIUS } from '@/constants/layout';

type AddLiquidityRangeInputProps = {
  initialValue: string;
  label: string;
  tokenPairLabel: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  fallback?: boolean;
  testID: string;
};

const InputStyles = css`
  font-weight: var(--fontWeight-body);
  font-family: var(--fontFamily-body);

  font-size: 28px;
  line-height: 36px;

  background: transparent;
  color: var(--color-fg);
  width: 100%;
  height: 36px;
  padding: 0;

  &::placeholder {
    color: rgb(var(--gray40));
  }

  // Hide default focus ring
  &:focus {
    outline: none;
  }
`;

const InputWrapperStyles = css`
  /* Fade out the right-most edge of input */
  mask-image: linear-gradient(to right, rgba(0, 0, 0, 1) 95%, rgba(0, 0, 0, 0) 100%);
  -webkit-mask-image: linear-gradient(to right, rgba(0, 0, 0, 1) 95%, rgba(0, 0, 0, 0) 100%);
`;

const WrapperStyles = css`
  box-shadow: 0 0 0 2px transparent;
  cursor: text;
  width: 100%;

  transition: box-shadow 75ms linear;
`;

const WrapperActiveStyles = css`
  border-color: var(--color-bgPrimary);
  box-shadow: 0 0 0 2px var(--color-bgPrimary);
`;

function AddLiquidityPriceInput({
  initialValue,
  label,
  tokenPairLabel,
  onChange,
  disabled,
  fallback,
  testID,
}: AddLiquidityRangeInputProps) {
  const [focused, setFocused] = useState(false);
  const handleFocus = useCallback(() => setFocused(true), []);
  const handleBlur = useCallback(() => setFocused(false), []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (onChange && isValidNumericInputValue(event.target.value)) {
        // Update the event target value (should we modify the value during validation)
        event.target.value = parseNumericInputValue(event.target.value);
        onChange(event);
      }
    },
    [onChange],
  );

  return (
    <VStack width="50%" flexGrow={1} flexShrink={1} gap={0.5}>
      {/* Label outside the bordered card */}
      <TextLabel1 as="p" color="fg" paddingY={0.5}>
        {label}
      </TextLabel1>

      {/* Bordered card containing input and token pair label */}
      <VStack
        className={cx(WrapperStyles, focused && WrapperActiveStyles)}
        bordered
        borderRadius={NESTED_CARD_BORDER_RADIUS}
        padding={2}
        gap={1}
      >
        <HStack alignItems="center" justifyContent="space-between" gap={1.5}>
          <Box className={InputWrapperStyles} opacity={disabled ? 0.5 : 1} flexGrow={1}>
            {!fallback && (
              <input
                className={InputStyles}
                placeholder={initialValue}
                type="text" // "text" lends the most flexibility for validation
                value={initialValue}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                disabled={disabled}
                data-testid={`${testID}-price-input`}
              />
            )}
            {fallback && <Fallback width="100%" height={36} shape="squircle" />}
          </Box>
        </HStack>
        {!fallback && (
          <TextLabel2 as="p" color="fgMuted">
            {tokenPairLabel}
          </TextLabel2>
        )}
        {fallback && <Fallback width={100} height={20} shape="squircle" />}
      </VStack>
    </VStack>
  );
}

/**
 * Price range inputs (current price text + Lowest/Highest inputs + out of range banner).
 * This renders BELOW the depth chart.
 * The title and SegmentedTabs are in AddLiquidityPriceRangeControls (above the chart).
 */
export const AddLiquidityPriceInputs = memo(function AddLiquidityPriceInputs({
  positionId,
}: {
  positionId: string | null;
}) {
  const {
    setTickPricesState,
    priceInputs,
    isFullRange,
    token0AsBaseToken,
    positionTokens,
    tickOutOfRange,
  } = useAddLiquidityState();
  const { isPhone, isPhoneLandscape } = useBreakpoints();
  const showMobileLayout = useMemo(() => isPhone ?? isPhoneLandscape, [isPhone, isPhoneLandscape]);
  const disabled = !!positionId || isFullRange;

  const tokenPairLabel = useMemo(
    () =>
      token0AsBaseToken
        ? `${positionTokens[1].symbol} per ${positionTokens[0].symbol}`
        : `${positionTokens[0].symbol} per ${positionTokens[1].symbol}`,
    [token0AsBaseToken, positionTokens],
  );

  const handleChangePriceLower = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setTickPricesState((prev) => ({
        priceLower: event.target.value ? event.target.value : '',
        priceUpper: prev.priceUpper,
      }));
    },
    [setTickPricesState],
  );

  const handleChangePriceUpper = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setTickPricesState((prev) => ({
        priceLower: prev.priceLower,
        priceUpper: event.target.value ? event.target.value : '',
      }));
    },
    [setTickPricesState],
  );

  return (
    <VStack gap={2} alignItems="flex-start">
      <HStack gap={0.5} justifyContent="center" flexWrap="wrap" width="100%">
        <TextLabel2 as="p" color="fgMuted" noWrap>
          Current price:
        </TextLabel2>
        <TextLabel1 as="p" noWrap>
          {format({ value: priceInputs.current.value, type: DisplayType.TokenPrice })}
        </TextLabel1>
        <TextLabel2 as="p" color="fgMuted" noWrap>
          {tokenPairLabel}
        </TextLabel2>
      </HStack>

      <HStack gap={2} width="100%" flexWrap={showMobileLayout ? 'wrap' : 'nowrap'}>
        <AddLiquidityPriceInput
          label="Lowest price"
          tokenPairLabel={tokenPairLabel}
          initialValue={isFullRange ? '0' : priceInputs.lower.value}
          onChange={handleChangePriceLower}
          disabled={disabled}
          testID="low"
        />

        <AddLiquidityPriceInput
          label="Highest price"
          tokenPairLabel={tokenPairLabel}
          initialValue={isFullRange ? '∞' : priceInputs.upper.value}
          onChange={handleChangePriceUpper}
          disabled={disabled}
          testID="high"
        />
      </HStack>

      {tickOutOfRange && (
        <Banner
          title="Out of range"
          variant="warning"
          startIcon="error"
          testID="out-of-range-warning-banner"
          startIconActive
        >
          Your position will not earn fees or be used in trades until the market price moves into
          your range.
        </Banner>
      )}
    </VStack>
  );
});

export const AddLiquidityPriceInputsLoading = memo(function AddLiquidityPriceInputsLoading() {
  const { isPhone, isPhoneLandscape } = useBreakpoints();
  const showMobileLayout = useMemo(() => isPhone ?? isPhoneLandscape, [isPhone, isPhoneLandscape]);

  return (
    <VStack gap={2} alignItems="flex-start">
      <HStack gap={0.5} justifyContent="center" flexWrap="wrap" width="100%">
        <TextLabel2 as="p" color="fgMuted" noWrap>
          Current price:
        </TextLabel2>
        <Fallback width={120} height={20} shape="squircle" />
        <Fallback width={90} height={20} shape="squircle" />
      </HStack>

      <HStack width="100%" gap={2} flexWrap={showMobileLayout ? 'wrap' : 'nowrap'}>
        <AddLiquidityPriceInput
          label="Lowest price"
          tokenPairLabel=""
          initialValue=""
          fallback
          testID="low-fallback"
        />

        <AddLiquidityPriceInput
          label="Highest price"
          tokenPairLabel=""
          initialValue=""
          fallback
          testID="high-fallback"
        />
      </HStack>
    </VStack>
  );
});
